/**
 * Tab Bestelling (keuze 15): de laatste bestelling groot, met ordernummer
 * (naar Lightspeed), bedrag, betaalwijze, een knop naar Picqer (werkplek
 * 28-10, alleen met Picqer-id), producten, de tijdlijn besteld, betaald,
 * verwerkt, verzonden, bezorgd met vinkjes op de echte stand, en per pakket
 * vervoerder, track en trace en status. Daaronder eerdere bestellingen
 * compact: datum, ordernummer met de stand ("bezorgd",
 * "retour, smaakwissel"; sessie 141-E) en bedrag. De producten staan in het
 * orderblok zodra je een eerdere bestelling opent (in dezelfde tab).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { productNaam, retourLink } from "../../../Admin/retourLink";

import { euro, veiligeLink } from "../opmaak";
import {
  kaart10Api,
  kaart10Sleutels,
  type KaartOrderTab,
  type KaartPakket,
  type KaartTicket,
} from "./api";
import { bestelStand, dagDatum, ticketReactie, ticketStand } from "./hulp";
import { PakketRegel, Stappen } from "./Tijdlijn10";

function Kar() {
  return (
    <svg className="kk10-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6h15l-1.5 9h-12z" />
      <circle cx="9" cy="20" r="1" />
      <circle cx="18" cy="20" r="1" />
      <path d="M6 6 5 3H2" />
    </svg>
  );
}

/** Lijn-icoon "openen in een ander venster" bij de knop Picqer. */
function Naarbuiten() {
  return (
    <svg className="kk10-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M14 4h6v6" />
      <path d="M20 4 10 14" />
      <path d="M19 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h6" />
    </svg>
  );
}

/** Opnieuw vragen zolang de vervoerder-vraag loopt: elke 2 s, hooguit 10 keer. */
export const OPHALEN_INTERVAL_MS = 2000;
export const OPHALEN_MAX = 10;
/** De backend bewaakt de 15 minuten; hier alleen geen dubbele vraag kort na elkaar. */
const PAKKETTEN_STALE_MS = 3 * 60 * 1000;

/**
 * Sessie 177: de pakketten van deze bestelling bij het openen ervan. Tot het
 * antwoord er is (of als de vraag mislukt) blijven de pakketten uit de lijst
 * staan: geen lege plek, geen foutmelding over de kaart.
 */
function usePakketten(klantId: string | undefined, orderId: string | null | undefined) {
  const queryClient = useQueryClient();
  const actief = Boolean(klantId && orderId);
  const sleutel = kaart10Sleutels.pakketten(klantId ?? "", orderId ?? "");
  // Tellen vanaf dit openen: wat er al in de cache stond telt niet mee.
  const [basis] = useState(() => queryClient.getQueryState(sleutel)?.dataUpdateCount ?? 0);
  const q = useQuery({
    queryKey: sleutel,
    queryFn: () =>
      kaart10Api.pakketten(
        klantId as string,
        orderId as string,
        (queryClient.getQueryState(sleutel)?.dataUpdateCount ?? 0) > basis,
      ),
    enabled: actief,
    staleTime: PAKKETTEN_STALE_MS,
    retry: 1,
    refetchInterval: (query) =>
      query.state.data?.ophalen && query.state.dataUpdateCount - basis < OPHALEN_MAX
        ? OPHALEN_INTERVAL_MS
        : false,
  });
  // dataUpdatedAt lezen laat react-query ook hertekenen als een gelijk
  // antwoord terugkomt (zelfde data, nieuwe poging).
  void q.dataUpdatedAt;
  const pogingen = (queryClient.getQueryState(sleutel)?.dataUpdateCount ?? 0) - basis;
  // Na de laatste poging loopt de vraag nog steeds: toon wat er is, zonder
  // "wordt opgehaald" (de stand blijft dan niet live bevestigd).
  const opgegeven = Boolean(q.data?.ophalen) && pogingen >= OPHALEN_MAX;
  // Eerste vraag nog onderweg: de regel zegt "wordt opgehaald" in plaats van
  // heel even de oude stand uit de lijst (teststand 177: geen knipperen).
  const laden = actief && q.data === undefined && q.isFetching;
  // Een live vraag is net klaar: de lijst (tijdlijn met bezorgdag) één keer
  // opnieuw laden, zodat bovenaan dezelfde dag staat als op de pakketregel.
  const vraagLiep = useRef(false);
  const klaar = q.data !== undefined && !q.data.ophalen;
  useEffect(() => {
    if (q.data?.ophalen) vraagLiep.current = true;
    else if (klaar && vraagLiep.current && klantId) {
      vraagLiep.current = false;
      void queryClient.invalidateQueries({ queryKey: kaart10Sleutels.bestellingen(klantId) });
    }
  }, [q.data, klaar, klantId, queryClient]);
  return { data: q.data, opgegeven, laden, fout: q.isError && q.data === undefined };
}

/**
 * TAP-tickets 28-10: één regel per ticket bij Cenfil, in de stijl van de
 * pakketregel. De stand staat als woord, met wanneer de vervoerder reageerde;
 * rechts de knop naar het ticket (zelfde vorm als de knop Picqer).
 */
function TicketRegel({ ticket }: { ticket: KaartTicket }) {
  const link = veiligeLink(ticket.url);
  const nummer = ticket.ticket_nummer?.trim().replace(/^#/, "") || null;
  return (
    <div className="kk10-pakket kk10-ticket" data-testid="ticket">
      <span className="n">Ticket bij Cenfil</span>
      <span className="w">
        {nummer ? <span className="num">#{nummer} · </span> : null}
        <span data-testid="ticket-stand">{ticketStand(ticket.status)}</span>
      </span>
      <span className="stil" data-testid="ticket-reactie">
        {ticketReactie(ticket.vervoerder_reactie_op)}
      </span>
      {link ? (
        <a
          className="kk10-picqer"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="order-ticket"
          title={
            nummer
              ? `Ticket #${nummer} openen bij Cenfil (nieuw tabblad)`
              : "Ticket openen bij Cenfil (nieuw tabblad)"
          }
        >
          <Naarbuiten />
          Ticket
        </a>
      ) : null}
    </div>
  );
}

function zonderOphalen(p: KaartPakket): KaartPakket {
  return p.live === "ophalen" ? { ...p, live: "niet_bevestigd" } : p;
}

export function Orderblok({
  order,
  laatste,
  mailadres = null,
  klantId,
}: {
  order: KaartOrderTab;
  laatste: boolean;
  /** Sessie 171: voor de knop "Retourneren" per bestelregel. */
  mailadres?: string | null;
  /** Sessie 177: voor de verse pakketstand van deze bestelling. */
  klantId?: string;
}) {
  const link = veiligeLink(order.lightspeed_url);
  // Werkplek 28-10: zonder Picqer-id geen knop (geen dode link).
  const picqer = veiligeLink(order.picqer_url ?? null);
  const { data: vers, opgegeven, laden, fout } = usePakketten(klantId, order.order_id);
  const pakketten = vers
    ? opgegeven
      ? vers.pakketten.map(zonderOphalen)
      : vers.pakketten
    : laden
      ? order.pakketten.map((p) => ({ ...p, live: "ophalen" as const }))
      : fout
        ? order.pakketten.map(zonderOphalen)
        : order.pakketten;
  const pakkettenReden = vers ? vers.pakketten_reden : order.pakketten_reden;
  return (
    <div className="kk10-blok" data-testid="orderblok">
      <div className="bh">
        <Kar />
        {laatste ? "Laatste bestelling" : "Bestelling"}{" "}
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            {order.ordernummer}
          </a>
        ) : (
          <span className="num">{order.ordernummer}</span>
        )}
        <span className="r num">
          {[euro(order.bedrag_cents), order.betaalmethode].filter(Boolean).join(" · ")}
        </span>
        {picqer ? (
          <a
            className="kk10-picqer"
            href={picqer}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="order-picqer"
            title={`Bestelling ${order.ordernummer} openen in Picqer (nieuw tabblad)`}
          >
            <Naarbuiten />
            Picqer
          </a>
        ) : null}
      </div>
      {order.geannuleerd ? <p className="kk10-melding">Geannuleerd</p> : null}
      <Stappen stappen={order.stappen} label={`Verloop van bestelling ${order.ordernummer}`} />
      {order.producten.length > 0 ? (
        <div className="bb">
          {order.producten.map((p) => (
            <span key={p} className="vol kk10-regel">
              <span className="kk10-regel-naam">{p}</span>
              {/* Sessie 171: één tik naar het formulier met deze regel
                  aangevinkt (bestelde aantal). */}
              <Link
                className="kk10-retourneer"
                to={retourLink({
                  ordernummer: order.ordernummer,
                  mailadres,
                  artikel: productNaam(p),
                })}
                data-testid="regel-retourneren"
                aria-label={`${productNaam(p)} retourneren`}
              >
                Retourneren
              </Link>
            </span>
          ))}
        </div>
      ) : null}
      {pakketten.map((p) => (
        <PakketRegel key={`${p.volgnummer}-${p.track_trace_code ?? ""}`} pakket={p} metBron />
      ))}
      {pakkettenReden ? (
        <div className="kk10-pakket">
          <span className="n">Pakket</span>
          <span className="kk10-onbekend">{pakkettenReden}</span>
        </div>
      ) : null}
      {(order.tickets ?? []).map((t, i) => (
        <TicketRegel key={`${t.url}-${i}`} ticket={t} />
      ))}
    </div>
  );
}

export function TabBestelling({
  klantId,
  mailadres = null,
}: {
  klantId: string;
  mailadres?: string | null;
}) {
  const [gekozen, setGekozen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: kaart10Sleutels.bestellingen(klantId),
    queryFn: () => kaart10Api.bestellingen(klantId),
  });
  // Sessie 141-E: de retouren uit dezelfde cache als de tab Retour (de kaart
  // haalt ze vooraf op), voor "retour, <reden>" bij een eerdere bestelling.
  const retouren = useQuery({
    queryKey: kaart10Sleutels.retouren(klantId),
    queryFn: () => kaart10Api.retouren(klantId),
    staleTime: 60_000,
  }).data?.retouren;

  if (q.isLoading) return <p className="stil">Bestellingen worden geladen.</p>;
  if (q.isError || !q.data)
    return (
      <p className="v6-fout" role="alert">
        De bestellingen konden niet geladen worden.
      </p>
    );
  const orders = q.data.orders;
  if (orders.length === 0) {
    return (
      <div className="kk10-blok">
        <div className="bb">
          <span className="vol stil">Nog geen bestelling.</span>
        </div>
      </div>
    );
  }
  const getoond = orders.find((o) => o.ordernummer === gekozen) ?? orders[0];
  const eerdere = orders.filter((o) => o.ordernummer !== getoond.ordernummer);
  return (
    <>
      <Orderblok
        key={getoond.ordernummer}
        order={getoond}
        laatste={getoond === orders[0]}
        mailadres={mailadres}
        klantId={klantId}
      />
      {eerdere.length > 0 ? (
        <div className="kk10-blok">
          <div className="bh">
            {getoond === orders[0] ? "Eerdere bestellingen" : "Andere bestellingen"}
            <span className="r">
              {q.data.heeft_meer
                ? `${orders.length} of meer, ook gekoppelde accounts`
                : `${orders.length} in de lijst, ook gekoppelde accounts`}
            </span>
          </div>
          <ul className="kk10-lijstje">
            {eerdere.map((o) => (
              <li key={o.ordernummer}>
                <button
                  type="button"
                  onClick={() => setGekozen(o.ordernummer)}
                  aria-label={`Bestelling ${o.ordernummer} openen`}
                >
                  <span className="d num">{dagDatum(o.besteld_op)}</span>
                  <span className="w">
                    <span className="nr">{o.ordernummer}</span>
                    <span data-testid="bestel-stand">{` · ${bestelStand(o, retouren)}`}</span>
                  </span>
                  <span className="num">{euro(o.bedrag_cents)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
